// Kontenverwaltung in der Anwendung.
//
// Kein Rechte- und Rollensystem: Wer angemeldet ist, darf Konten anlegen und
// verwalten. Der Server hält die Sicherungen dagegen, dass sich jemand selbst
// oder das letzte Konto aussperrt; die Oberfläche blendet die Schaltflächen
// dafür gar nicht erst ein.

import { useEffect, useState } from "react";
import { api, ApiFehler } from "../api.ts";
import { datum } from "../formate.ts";
import { Sheet, TextFeld, HakenFeld, Leer } from "../komponenten/Bausteine.tsx";
import { useZustand } from "../zustand.tsx";
import type { Benutzerkonto } from "../typen.ts";

/**
 * Zeigt ein frisch erzeugtes Passwort — genau einmal. Danach steht in der
 * Datenbank nur der Hash, es lässt sich nirgends nachschlagen. Deshalb bleibt
 * dieses Blatt offen, bis es ausdrücklich geschlossen wird.
 */
function PasswortBlatt(
  { email, passwort, aufSchliessen }:
  { email: string; passwort: string; aufSchliessen: () => void },
) {
  const { zeigeMeldung } = useZustand();
  return (
    <Sheet
      titel="Passwort notieren"
      aufSchliessen={aufSchliessen}
      fuss={<div className="r">
        <button className="btn btn-primary" onClick={aufSchliessen}>
          Habe ich notiert
        </button>
      </div>}
    >
      <p className="sub" style={{ margin: "0 0 14px" }}>
        Passwort für <b>{email}</b>. Es wird <b>nur dieses eine Mal</b> angezeigt —
        danach liegt in der Datenbank nur noch ein Hash. Jetzt in den
        Passwortmanager übernehmen oder weitergeben.
      </p>
      <div style={{
        background: "var(--blue-tint)", color: "var(--ink)", borderRadius: "var(--radius-s)",
        padding: "16px 18px", fontSize: 19, fontWeight: 700, letterSpacing: 0.5,
        wordBreak: "break-all", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}>
        {passwort}
      </div>
      <button className="btn btn-quiet btn-sm" style={{ marginTop: 12 }}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(passwort);
            zeigeMeldung("In die Zwischenablage kopiert");
          } catch {
            zeigeMeldung("Kopieren nicht möglich — bitte von Hand übernehmen.", true);
          }
        }}>
        Kopieren
      </button>
    </Sheet>
  );
}

function NeuesKontoBlatt(
  { aufFertig, aufSchliessen }:
  { aufFertig: (email: string, passwort: string | null, konten: Benutzerkonto[]) => void; aufSchliessen: () => void },
) {
  const { zeigeMeldung } = useZustand();
  const [email, setzeEmail] = useState("");
  const [name, setzeName] = useState("");
  const [eigenes, setzeEigenes] = useState(false);
  const [passwort, setzePasswort] = useState("");
  const [laeuft, setzeLaeuft] = useState(false);

  return (
    <form onSubmit={async (e) => {
      e.preventDefault();
      if (!email.trim() || !name.trim()) return;
      if (eigenes && passwort.length < 12) {
        zeigeMeldung("Das Passwort muss mindestens 12 Zeichen lang sein.", true);
        return;
      }
      setzeLaeuft(true);
      try {
        const ergebnis = await api.kontoAnlegen({
          email: email.trim(), name: name.trim(),
          ...(eigenes ? { passwort } : {}),
        });
        aufFertig(ergebnis.benutzer.email, ergebnis.erzeugtesPasswort, ergebnis.alle);
      } catch (fehler) {
        zeigeMeldung(fehler instanceof ApiFehler ? fehler.message : "Anlegen fehlgeschlagen.", true);
      } finally {
        setzeLaeuft(false);
      }
    }}>
      <Sheet
        titel="Neues Konto"
        aufSchliessen={aufSchliessen}
        fuss={<div className="r">
          <button className="btn btn-quiet" type="button" onClick={aufSchliessen}>Abbrechen</button>
          <button className="btn btn-primary" type="submit" disabled={laeuft}>
            {laeuft ? "Legt an …" : "Konto anlegen"}
          </button>
        </div>}
      >
        <div className="form-grid">
          <TextFeld titel="Name *" voll wert={name} aendern={setzeName} platzhalter="z. B. Anna" />
          <TextFeld titel="E-Mail *" voll typ="email" wert={email} aendern={setzeEmail}
            platzhalter="anna@example.de" />
          <HakenFeld titel="Passwort selbst vergeben" wert={eigenes} aendern={setzeEigenes} />
          {eigenes ? (
            <TextFeld titel="Passwort (mindestens 12 Zeichen)" voll typ="password"
              wert={passwort} aendern={setzePasswort} />
          ) : null}
        </div>
        <p className="hint">
          Ohne eigenes Passwort wird eines erzeugt und danach einmalig angezeigt.
          Es gibt keine Einladungs-Mail — das Passwort gibst du selbst weiter.
        </p>
      </Sheet>
    </form>
  );
}

export function BenutzerVerwaltung() {
  const { benutzer: angemeldet, zeigeMeldung } = useZustand();
  const [konten, setzeKonten] = useState<Benutzerkonto[] | null>(null);
  const [eigeneId, setzeEigeneId] = useState("");
  const [neuOffen, setzeNeuOffen] = useState(false);
  const [gezeigtesPasswort, setzeGezeigtesPasswort] =
    useState<{ email: string; passwort: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const daten = await api.konten();
        setzeKonten(daten.benutzer);
        setzeEigeneId(daten.eigeneId);
      } catch {
        zeigeMeldung("Die Kontenliste ließ sich nicht laden.", true);
        setzeKonten([]);
      }
    })();
  }, [zeigeMeldung]);

  const fuehreAus = async (aktion: () => Promise<Benutzerkonto[]>, text: string) => {
    try {
      setzeKonten(await aktion());
      zeigeMeldung(text);
    } catch (fehler) {
      zeigeMeldung(fehler instanceof ApiFehler ? fehler.message : "Nicht möglich.", true);
    }
  };

  return (
    <>
      <div className="more-sec" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span>Konten</span>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }}
          onClick={() => setzeNeuOffen(true)}>+ Konto</button>
      </div>

      {konten === null ? (
        <div className="sub">Wird geladen …</div>
      ) : konten.length === 0 ? (
        <Leer titel="Keine Konten." />
      ) : konten.map((k) => {
        const binIch = k.id === eigeneId;
        return (
          <div className="row-card" key={k.id}>
            <div style={{ minWidth: 200 }}>
              <div className="row-title">
                {k.name}
                {binIch ? <span className="chip vermietbar">du</span> : null}
                {!k.aktiv ? <span className="chip hoch">gesperrt</span> : null}
              </div>
              <div className="row-sub">
                {k.email}
                {" · angelegt "}{datum(k.erstellt_am)}
                {k.letzter_login ? ` · zuletzt angemeldet ${datum(k.letzter_login)}` : " · noch nie angemeldet"}
              </div>
            </div>

            <div className="row-actions" style={{ flexWrap: "wrap" }}>
              <button className="btn btn-quiet btn-sm" onClick={async () => {
                const name = prompt("Name", k.name);
                if (name === null || name.trim() === k.name) return;
                await fuehreAus(
                  async () => (await api.kontoAendern(k.id, { name })).benutzer, "Umbenannt");
              }}>Umbenennen</button>

              <button className="btn btn-quiet btn-sm" onClick={async () => {
                const frage = binIch
                  ? "Neues Passwort für dein eigenes Konto erzeugen?\n\n"
                    + "Du bleibst angemeldet, aber auf allen anderen Geräten musst du "
                    + "dich künftig mit dem neuen Passwort anmelden. Notiere es sofort — "
                    + "es wird nur einmal angezeigt."
                  : `Neues Passwort für ${k.email} erzeugen?\n\n`
                    + "Alle Sitzungen dieses Kontos werden dabei beendet — der Weg, "
                    + "wenn ein Gerät verloren gegangen ist.";
                if (!confirm(frage)) return;
                try {
                  // Beim eigenen Konto die laufende Sitzung behalten, sonst
                  // fliegt man im selben Moment hinaus, in dem das Passwort
                  // angezeigt wird.
                  const { erzeugtesPasswort } = await api.kontoPasswort(k.id,
                    binIch ? { sitzungenBeenden: false } : {});
                  if (erzeugtesPasswort) {
                    setzeGezeigtesPasswort({ email: k.email, passwort: erzeugtesPasswort });
                  }
                } catch (fehler) {
                  zeigeMeldung(fehler instanceof ApiFehler ? fehler.message : "Nicht möglich.", true);
                }
              }}>Passwort neu</button>

              {/* Die beiden folgenden würde der Server für das eigene Konto
                  ohnehin ablehnen — gar nicht erst anzubieten ist ehrlicher
                  als eine Fehlermeldung nach dem Klick. */}
              {binIch ? null : (
                <>
                  <button className="btn btn-quiet btn-sm" onClick={() => void fuehreAus(
                    async () => (await api.kontoAendern(k.id, { aktiv: !k.aktiv })).benutzer,
                    k.aktiv ? "Gesperrt" : "Freigegeben")}>
                    {k.aktiv ? "Sperren" : "Freigeben"}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => {
                    if (!confirm(`Konto ${k.email} endgültig löschen?\n\n`
                      + "Die Portfoliodaten bleiben unberührt — nur der Zugang verschwindet.")) return;
                    void fuehreAus(async () => (await api.kontoLoeschen(k.id)).benutzer, "Konto gelöscht");
                  }}>Löschen</button>
                </>
              )}
            </div>
          </div>
        );
      })}

      <p className="sub" style={{ marginTop: 8 }}>
        Alle Konten dürfen alles — es gibt bewusst keine Rollen. Was der Server
        verhindert: das eigene Konto sperren oder löschen und das letzte Konto
        entfernen. Sonst käme niemand mehr hinein.
        {angemeldet ? ` Angemeldet als ${angemeldet.email}.` : ""}
      </p>

      {neuOffen ? (
        <NeuesKontoBlatt
          aufSchliessen={() => setzeNeuOffen(false)}
          aufFertig={(email, passwort, alle) => {
            setzeKonten(alle);
            setzeNeuOffen(false);
            if (passwort) setzeGezeigtesPasswort({ email, passwort });
            else zeigeMeldung("Konto angelegt");
          }}
        />
      ) : null}

      {gezeigtesPasswort ? (
        <PasswortBlatt
          email={gezeigtesPasswort.email}
          passwort={gezeigtesPasswort.passwort}
          aufSchliessen={() => setzeGezeigtesPasswort(null)}
        />
      ) : null}
    </>
  );
}
